import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_LOCALE,
  LOCALE_INTL,
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  translate,
} from '../i18n/translations';
import api from '../utils/api';

const Ctx = createContext({
  locale: DEFAULT_LOCALE,
  setLocale: () => {},
  t: (k) => k,
  intlLocale: LOCALE_INTL[DEFAULT_LOCALE],
});

// eslint-disable-next-line react-refresh/only-export-components
export const useLocale = () => useContext(Ctx);

// eslint-disable-next-line react-refresh/only-export-components
export const useT = () => useContext(Ctx).t;

const STORAGE_KEY = 'virgy.locale';
const STORAGE_SOURCE_KEY = 'virgy.locale.source';  // 'explicit' once user picked one

function localStored() {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored && SUPPORTED_LOCALES.includes(stored) ? stored : null;
}
function navDetect() {
  const nav = (navigator.language || DEFAULT_LOCALE).slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALES.includes(nav) ? nav : DEFAULT_LOCALE;
}

/* Initial guess · uses an explicit local override if present, otherwise
 * navigator. The employee's `preferred_locale` (when available) overrides
 * the nav guess once the API responds, but never overrides an explicit
 * user choice on this device. */
function initialLocale() {
  return localStored() || navDetect();
}

export default function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(initialLocale);
  // Whether the current locale came from an explicit user pick on this
  // device. If yes, we don't let the employee.preferred_locale stomp it.
  const explicitRef = useRef(localStorage.getItem(STORAGE_SOURCE_KEY) === 'explicit');

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  // Once authenticated, hydrate the locale from the employee's preference
  // unless the user has already overridden it on this device.
  useEffect(() => {
    let cancelled = false;
    api.myEmployee().then(emp => {
      if (cancelled || !emp) return;
      const pref = (emp.preferred_locale || '').trim().toLowerCase();
      if (pref && SUPPORTED_LOCALES.includes(pref) && !explicitRef.current) {
        setLocaleState(pref);
        localStorage.setItem(STORAGE_KEY, pref);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) return;
    setLocaleState(next);
    localStorage.setItem(STORAGE_KEY, next);
    localStorage.setItem(STORAGE_SOURCE_KEY, 'explicit');
    explicitRef.current = true;
    // Persist on the employee row so other devices pick it up. Best-effort.
    api.updateMyLocale(next).catch(() => {});
  }, []);

  const value = useMemo(() => ({
    locale,
    setLocale,
    t: (key, vars) => translate(locale, key, vars),
    intlLocale: LOCALE_INTL[locale],
    labels: LOCALE_LABELS,
    supported: SUPPORTED_LOCALES,
  }), [locale, setLocale]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
